<template>
    <div class="px-8 relative">
        <div v-if="reg.Installer && reg.Device" class="absolute top-0 right-4 w-64 hidden xl:flex z-50 flex-col gap-2">
            <div class="bg-gray-300 p-2 rounded-lg pb-4 border-gray-400 border relative">
                <InformationCircleIcon class="h-6 w-6 text-gray-500 top-2 right-2 absolute"/>
                <div class="pb-4"/>
                <small-info-box label="Installer &amp; Company" :modelValue="reg.Installer.firstName + ' ' + reg.Installer.lastName + ' (' + reg.Installer.company + ')'"></small-info-box>
                <small-info-box label="Installer FGAS" :modelValue="reg.Installer.fgas"></small-info-box>
                <small-info-box label="Model" :modelValue="reg.Device.DeviceModel ? reg.Device.DeviceModel.name : ('\'' + reg.Device.model + '\'')"></small-info-box>
                <small-info-box label="Purchasing Company" :modelValue="reg.Device.Company.name"></small-info-box>
                <small-info-box label="Purchasing Company Email" :modelValue="reg.Device.Company.email"></small-info-box>
                <small-info-box
                    label="Distribution Parent"
                    :modelValue="reg.Device.Company.isDistributor
                                 ? reg.Device.Company.name
                                 : reg.Device.Company.DistributionParent ? reg.Device.Company.DistributionParent.name : 'Midea'">
                </small-info-box>
                <div v-if="reg.Customer">
                    <small-info-box label="Customer Email" :detail="`(${reg.Customer.name})`" :detailUrl="`/#/company/${reg.Customer.id}`" :modelValue="reg.Customer.email"></small-info-box>
                    <div class="text-xs text-red-600 pt-2" v-if="!reg.Customer.email || reg.Customer.email.length === 0">Invalid email. You will need to enter a valid email address before approving registrations.</div>
                </div>
            </div>

            <div class="bg-yellow-100 rounded-lg border-yellow-400 border relative text-yellow-900 text-sm pb-2">
                <ChatAlt2Icon class="h-6 w-6 text-yellow-900 top-2 right-2 absolute"/>
                <div class="m-2 pt-1 font-medium">Notes</div>
    
                <div v-if="loadingNotes">Loading...</div>

                <template v-if="!loadingNotes">
                <ul class="flex flex-col">
                    <li class="m-2 text-xs" v-for="note, idx in notes" :key="note.id">
                        <div v-html="note.content.replaceAll('\n', '<br>')"></div>
                        <div class="flex pt-1">
                            <div class="opacity-50 flex-grow">{{ note['User.firstName'] }}</div>
                            <div class="font-semibold text-right flex-shrink">{{ moment(note.date).format('D/MM/YYYY') }}</div>
                        </div>
                        <div v-if="idx != notes.length - 1" class="border-b border-yellow-300 mt-2"></div>
                    </li>
                </ul>

                <div class="mt-2"></div>
                <div v-if="!addingNote" @click="addingNote=true" class="flex flex-col items-center"><button class="mx-auto bg-yellow-200 text-xs p-2 rounded-lg px-4 transition hover:bg-yellow-300">Add Note...</button></div>

                <form v-if="addingNote" @submit.prevent="submitNote">
                    <div class="mx-2"><textarea v-model.trim="noteToAdd" v-if="addingNote" class="w-full bg-yellow-100 border-yellow-800 rounded-md text-xs focus:ring-yellow-900 focus:outline-none"></textarea> </div>
                    <div v-if="addingNote"  class="flex flex-col items-center"><button class="mx-auto bg-yellow-200 text-xs p-2 rounded-lg px-4 transition hover:bg-yellow-300 focus:ring-yellow-900">Submit</button></div>
                </form>
                </template>

            </div>

        </div>

        <form-header title="Registration" 
                    :subtitle="regSummary">
                <div>
                    <div class="flex">
                        <custom-button class="w-56" :type="canApproveRegistration ? 'primary' : 'disabled'" 
                                    @click="approveRegistration" :disabled="!canApproveRegistration">
                                    Approve Registration<img v-if="approvingRegistration" class="mx-2" src="@/assets/loading-spinner.svg">
                                    </custom-button>
                        
                        <custom-popup-menu class="pt-2">
                            <custom-popup-menu-item title="Delete Registration" @click="onDeleteRegistration"></custom-popup-menu-item>
                        </custom-popup-menu>

                    </div>
                    <div v-if="regError" class="text-gray-700 text-sm pt-2">{{regError}}</div>
                </div>
        </form-header>

        <form-div v-if="!canRegisterIfService" class="pb-4">
            <div class="border border-red-200 rounded-md bg-red-50 text-red-700 p-4 text-sm flex gap-2">
                <div class="flex-shrink-0">
                    <ExclamationCircleIcon class="h-5 w-5 text-red-700" aria-hidden="true" />
                </div>
                <div class="">{{ serviceRegistrationError }}</div>
            </div>
        </form-div>

        <form-input id="reg-data" label="Date of Registration" type="date" v-model="regDate"/>
        <form-input id="purchasedate" type="text" label="Purchase Date" readonly="true" :modelValue="momentDate(reg?.Device?.date)"/>
        <form-select id="reg-type" label="Registration Type" v-model="reg.type" :options="registrationTypes"/>
        <form-input v-if="reg.type == 'service'" id="service-year" label="Service Year" :error="msg.serviceYear" v-model.trim="reg.serviceYear"/>

        <form-input v-if="reg.type == 'warranty' || reg.type == 'points'" id="reg-miPoints" type="number" label="Mi Points Earned" :error="msg.points" v-model.trim="reg.points"/>
        <form-input v-if="reg.type == 'warranty' || reg.type == 'points' || reg.type == 'labourCredit'" id="reg-mcpoints" type="number" label="Mi Care Points Earned" :error="msg.miCarePoints" v-model.trim="reg.miCarePoints"/>
        <form-input v-if="reg.type == 'labourCredit'" id="reg-custref" label="Customer Reference" :error="msg.job_ref" v-model.trim="reg.job_ref"/>

        <div v-if="reg.type == 'warranty'">
            <form-input id="reg-installed-company" label="Customer (As Entered)" :error="msg.customer" v-model.trim="reg.customer"/>
            <form-input id="reg-installed-company-notes" label="Notes" labelClass="ml-4" :readonly="true" :error="msg.customerNotes" :modelValue="reg.customerNotes"/>
            <form-select id="reg-installed-company-id" label="Customer (Link)" :error="(reg.customerId && reg.customerId != 0 && reg.customerId != -1) ? '' : 'The registration must be linked to a customer. If you can\'t find the customer in this list you will need to create it first in the Companies menu'" v-model.trim="reg.customerId" :options="companies"/>
        </div>

        <form-div v-if="reg.Device && reg.Device.DeviceModel && reg.Device.DeviceModel.isHeatPump" label="Installation Address">
            <textarea @input="updated" class="h-24 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm text-gray-700" v-model.trim="reg.address">
            </textarea>
        </form-div>

        <form-select v-if="reg.type == 'warranty' || reg.type == 'points'" id="reg-points-company-id" label="Points Assigned To" v-model.trim="reg.pointsCompanyId" :options="pointsCompanies"/>
        
        <form-toggle label="No access to site" v-model="reg.noAccessToSite" readonly="true"/>
        <form-toggle v-if="isMCSVisible" label="Installation is MCS Accredited" v-model="reg.mcsAccredited" readonly="true"/>

        <div v-if="reg.type === 'part'">
            <form-input id="partno" type="text" readonly="true" label="Part No" :error="msg.partNo" v-model.trim="reg.partNo"/>
            <form-input id="dateSent" type="text" readonly="true" label="Date Sent" :error="msg.partDateSent" :modelValue="momentDate(reg.partDateSent)"/>
            <form-input id="parttype" type="text" readonly="true" label="Part Type" :error="msg.partType" v-model.trim="reg.partType"/>
            <form-select id="partsel" readonly="true" label="Purchase / Warranty" :error="msg.partSel" v-model="reg.partSel" :options="partSelectTypes"/>
        </div>

        <div v-if="reg.type === 'partDispatched' || reg.type === 'partFitted'">
            <form-input id="partCode" type="text" readonly="true" label="Part Code" :modelValue="reg.partCode"/>
            <form-input id="partDetail" type="text" readonly="true" label="Part Description" :modelValue="reg.partDetail"/>
            <form-input id="partCompanyName" type="text" readonly="true" label="Company Name" :modelValue="reg.customer"/>
            <form-input id="partContactName" type="text" readonly="true" label="Contact Name" :modelValue="reg.contactName"/>
            <form-div v-if="reg.deliveryAddress" label="Delivery Address">
                <textarea class="h-24 block w-full rounded-md border-gray-300 shadow-sm text-sm text-gray-700" readonly :value="reg.deliveryAddress"></textarea>
            </form-div>
        </div>

        <div v-if="reg.type === 'repair'">
            <form-text-area id="faultDescription" label="Repair Description" :error="msg.faultDetail" v-model="reg.faultDetail" />
            <form-text-area id="repairNotes" label="Parts Used" :error="msg.notes" v-model="reg.notes" />
            <form-toggle id="fittedIn72Hours" label="Fitted within 72 hours" v-model="reg.fittedIn72Hours" />
            <form-toggle id="labourAllowanceClaim" label="Claim labour allowance" v-model="reg.labourAllowanceClaim" />
        </div>

        <div v-if="reg.type == 'warranty'">

            <form-input id="reg-cert-email" label="Certificate Email" :error="msg.cert_email" v-model.trim="reg.cert_email"/>
            <form-input id="reg-extraPiping" type="text" label="Extra Piping" :error="msg.pipingLength" v-model.trim="reg.pipingLength"/>
            <form-input id="reg-custref" label="Customer Reference" :error="msg.job_ref" v-model.trim="reg.job_ref"/>
            <form-input id="reg-notes" label="Notes" :error="msg.notes" v-model.trim="reg.notes"/>
            <form-input id="reg-application" label="Application" :error="msg.application" v-model.trim="reg.application"/>
        </div>

        <form-div v-if="changed" class="py-2">
            <custom-button type="primary" @click="save" class="w-24 mr-2">Save</custom-button>
            <custom-button type="white" @click="cancel" class="w-24">Cancel</custom-button>
        </form-div>

        <div v-if="reg.type != 'points'">
            <form-div v-if="!reg.approved" label="Certificate">
                Certifcate has not been generated<br>
                Generate the certificate by approving the registration
            </form-div>

            <form-div v-if="reg.approved" label="Certificate">
                <div>Approved {{ moment(reg.approvedByDate).fromNow()}} by 
                    <span v-if="reg.User">{{reg.User.firstName}} {{reg.User.lastName}}</span>
                    <span v-if="!reg.User">Unknown User</span>
                </div>
                <div class="flex gap-2 mt-2 w-full">
                    <custom-button class="w-16" type="white" @click="onDownloadCertButton">
                        <DownloadIcon class="h-6 w-6 text-gray-800"/>
                    </custom-button>
                    <custom-button class="flex-grow" type="white" @click="recreateCertificate">
                        <RefreshIcon class="h-6 w-6 text-gray-800"/>
                        <span class="pl-2">Recreate certificate</span>
                    </custom-button>
                </div>
                <div class="flex gap-2 mt-2">
                    <custom-button class="w-full" type="white" @click="approveRegistration">
                        <RefreshIcon class="h-6 w-6 text-gray-800"/>
                        <span class="pl-2">Recreate and resend certificate</span>
                    </custom-button>
                </div>
            </form-div>
        </div>

        <div v-if="reg.checklist" class="pt-8">
            <form-header title="Registration Checklist">
                <div class="w-full">
                    <ul class="flex flex-col gap-1 text-gray-700">
                        <li v-for="cl in checklistValues" :key="cl.id">
                            <div class="flex items-top">
                                <input type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" :value="cl.text" disabled :checked="cl.value">
                                <div class="ml-3 text-sm">{{cl.text}}</div>
                            </div>
                        </li>
                    </ul>

                    <div class="my-2 text-sm font-medium">Comments</div>
                    <textarea class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm text-gray-500" readonly :value="reg.checklistComments">
                    </textarea>
                </div>
            </form-header>
        </div>

        <div v-if="reg.Installer" class="pt-8">
            <form-header 
                        title="Installer" 
                        :subtitle="instSummary"
                        :titleLink="`/installer/${reg.Installer.id}`">
                <template v-slot:titleIcon>
                    <PencilAltIcon class="h-6 w-6 text-gray-800"/>
                </template>
                <div class="flex flex-col items-end">
                    <custom-button :type="needVerifyUser ? 'primary' : 'disabled'" class="w-48" 
                               @click="verifyInstaller" :disabled="!needVerifyUser">Verify Installer</custom-button>
                    <div v-if="instFGASError" class="text-red-700 leading-5 pt-2 pb-1">
                        This installer can not be verified as the FGAS number is missing or incorrect
                    </div>
                </div>  
            </form-header>
            <form-input id="inst-fgas" label="FGAS" :modelValue="reg.Installer.fgas" readonly="true"/>
            <form-input id="inst-firstName" label="First Name" :modelValue="reg.Installer.firstName" readonly="true"/>
            <form-input id="inst-lastName" label="Last Name" :modelValue="reg.Installer.lastName" readonly="true"/>
            <form-input id="inst-company" label="Company" :modelValue="reg.Installer.company" readonly="true"/>
            <form-input id="inst-tel" label="Tel" :modelValue="reg.Installer.tel" readonly="true"/>
            <form-input id="inst-email" label="Email" :modelValue="reg.Installer.email" readonly="true"/>
        </div>

        <div v-if="reg.Device && (reg.type == 'warranty' || reg.type =='service' || reg.type == 'part' || reg.type === 'repair')" class="pt-8">
            <form-header 
                        title="Installed device" 
                        :titleLink="`/device/${reg.Device.id}`"
                        >
                <template v-slot:titleIcon>
                    <PencilAltIcon class="h-6 w-6 text-gray-800"/>
                </template>
            </form-header>
            <form-input id="dev-serial" label="Serial Number" :modelValue="reg.Device.serial" readonly="true"/>
            <form-input id="dev-model" label="Model" :modelValue="reg.Device.DeviceModel ? reg.Device.DeviceModel.name : ('\'' + reg.Device.model + '\'')" readonly="true"/>
            <form-toggle id="dev-warrantyValid" label="Warranty Valid" :modelValue="reg.Device.warrantyValid" readonly="true"/>
            <form-input v-if="reg.Device.warrantyRegistered" id="dev-date" label="Warranty Registration Date" :modelValue="momentDate(reg.Device.warrantyRegistrationDate)" readonly="true"/>
            <form-input v-if="reg.Device.warrantyRegistered" id="dev-warrantyDate" label="Warranty Expiry Date" :modelValue="momentDate(reg.Device.warrantyExpiryDate)" readonly="true"/>
            <!-- <form-input id="dev-warrantyEmail" label="Warranty Email" :modelValue="reg.Device.warrantyEmail" readonly="true"/> -->
            <form-input id="dev-purchasing-company" label="Purchasing Company" :modelValue="reg.Device.Company.name" readonly="true"/>
            <form-input id="dev-purchasing-company-email" label="Purchasing Company Email" :modelValue="reg.Device.Company.email" readonly="true"/>
            <form-input id="dev-installation-company" label="Installation Company" :modelValue="reg.Device.Customer ? reg.Device.Customer.name : ''" readonly="true"/>
            <form-image id="dev-iamge" label="Images" :images="images"/>
        </div>

        <div v-if="(reg.type == 'warranty' || reg.type == 'service') && reg.installPhotos  && (canRegisterIfService)" class="pt-8">
            <form-header 
                        title="Registration Photographs"
                        :subtitle="regPhotoSummary"
                        />
            <form-image v-if="reg.installPhotos.length" id="dev-iamge" label="Photographs" :images="reg.installPhotos"/>
        </div>

    </div>
</template>

<script>
import FormHeader from '../components/FormHeader.vue'
import FormInput from '../components/FormInput.vue'
import SmallInfoBox from '../components/SmallInfoBox.vue'
import FormSelect from '../components/FormSelect.vue'
import FormImage from '../components/FormImage.vue'
import FormToggle from '../components/FormToggle.vue'
import FormTextArea from '../components/FormTextArea.vue'
import CustomButton from '../components/CustomButton.vue'
import FormDiv from '../components/FormDiv.vue'
import CustomPopupMenu from '../components/CustomPopupMenu.vue'
import CustomPopupMenuItem from '../components/CustomPopupMenuItem.vue'
import { DownloadIcon, RefreshIcon } from '@heroicons/vue/solid';
import { PencilAltIcon, InformationCircleIcon, ExclamationCircleIcon, ChatAlt2Icon } from '@heroicons/vue/outline';
import DownloadFile from '../DownloadFile.js'
import moment from 'moment'
import _ from 'lodash';

export default {
    components: { 
        FormHeader, 
        FormInput, 
        SmallInfoBox,
        FormImage, 
        FormSelect, 
        FormToggle, 
        CustomButton, 
        FormDiv, 
        DownloadIcon, 
        RefreshIcon, 
        PencilAltIcon,
        InformationCircleIcon,
        CustomPopupMenu,
        CustomPopupMenuItem,
        ExclamationCircleIcon,
        ChatAlt2Icon,
        FormTextArea
     },
    data: function() {
        return {
            reg: {},
            data: {},
            msg: {},
            images: [],
            companies: [],
            pointsCompanies: [],
            changed: false,
            approvingRegistration: false,

            addingNote: false,
            noteToAdd: '',
            notes: [],
            loadingNotes: false
        }
    },
    computed: {
        isMCSVisible() {
            return this.reg && this.reg.type === 'service' 
                && this.reg.Device && this.reg.Device.DeviceModel && this.reg.Device.DeviceModel.documentSet == 14
        },
        registrationTypes() {
            return [
                { id: 'warranty', name: 'Warranty' },
                { id: 'service', name: 'Service' },
                { id: 'points', name: 'Points Redemption' },
                { id: 'labourCredit', name: 'Labour Credit' },
                { id: 'part', name: 'Part Exchanged' },
                { id: 'repair', name: 'Repair Performed'},
                { id: 'partDispatched', name: 'Part Dispatched'},
                { id: 'partFitted', name: 'Part Fitted'}
            ]
        },
        checklistValues() {
            return JSON.parse(this.reg.checklist)
        },        
        partSelectTypes() {
            return [
                { id: 'warranty', name: 'Warranty' },
                { id: 'purchase', name: 'Purchase' }
            ]
        },
        regSummary() {
            return `Submitted ${this.moment(this.reg.date).fromNow()}`
        },
        instSummary() {
            if (this.reg.Installer.verified) return `Last verified ${this.moment(this.reg.Installer.lastVerified).fromNow()}`
            else if (this.reg.Installer.fgas.length <= 3) return ''
            else return 'This installer has not been previously verified'
        },
        instFGASError() {
            return !this.reg.Installer.verified && this.reg.Installer.fgas.length <= 3
        },
        regPhotoSummary() {
            return (this.reg.installPhotos.length > 0) ? null : 'There are no photographs associated to this registration'
        },
        warrantyValid() {
            const today = new Date()
            const warrantyExpiredPlusAllowance = new Date(this.reg.Device.warrantyExpiryDate)
            warrantyExpiredPlusAllowance.setMonth(warrantyExpiredPlusAllowance.getMonth() + 3)
            return today < warrantyExpiredPlusAllowance
        },
        canRegisterIfService() {
            if (this.reg.type !== 'service') {
                return true
            }

            // Not registered for warranty
            if (this.reg.Device && !this.reg.Device.warrantyRegistered) {
                return false
            }

            if (!this.warrantyValid) {
                return false
            }

            if (!this.reg.approved && this.reg.serviceYear != this.reg.Device.serviceYear+1) { 
                return false
            }

            return true
        },
        serviceRegistrationError() {
            if (this.reg.Device && !this.reg.Device.warrantyRegistered) { return 'Device being registered for service, but has no prior warranty registration.' }
            if (!this.warrantyValid) { return 'Device is out of warranty. Approving this will reset the warranty date.' }
            if (this.reg.serviceYear != this.reg.Device.serviceYear+1) { return 'Service year looks incorrect, please check device history before approving.' }
            return ''
        },
        canApproveRegistration() {
            if (this.reg.type === 'repair') {
                return false
            }

            if (this.changed) {
                return false;
            }

            if (!this.reg || !this.reg.Installer || !this.reg.Installer.verified || this.reg.approved) {
                return false;
            }

            if (this.reg.type == 'warranty' && !this.reg.customerId) {
                return false;
            }

            if (this.reg.type == 'warranty') {
                if (!this.reg.Customer || this.reg.Customer.id != this.reg.customerId) {
                    return false;
                }

                if (!this.reg.Customer.email || this.reg.Customer.email.length == 0 || !this.$validEmail(this.reg.Customer.email)) {
                    return false;
                }

                if (!this.reg.Device.modelId) {
                    return false;
                }
            }

            return this.isUserVerified()
        },
        awaitingDistributorApproval() {
            return this.reg.type==='warranty' && this.reg.Device.Company.isDistributor && !this.reg.distributorApproved
        },
        regError() {
            if (this.reg && this.reg.Device && !this.reg.Device.modelId) {
                return 'Please assign the correct model to the device before approving the registration'
            }
            return null
        },
        needVerifyUser() {
            return !this.isUserVerified() && this.reg.Installer.fgas.length > 3
        },
        regDate: {
            get() {
                return moment(this.reg.date).format('yyyy-MM-DD')
            },
            set(val) {
                this.reg.date = new Date(val)
            }
        }
    },
    props: [
        'id',
        'prev'
    ],
    methods: {
        updateLocalCopy() {
            if (!this.data.points && this.data.points !== 0) {
                this.data.points = ''
            }

            if (!this.data.pipingLength) {
                this.data.pipingLength = ''
            }
            this.reg = Object.assign(Object.create(Object.getPrototypeOf(this.data)), this.data)
        },
        async refreshData() {
            let query = await this.axios.get(`/registration/${this.id}`)
            query.data.installPhotos = this.reg.installPhotos
            this.data = query.data
            this.updateLocalCopy()
        },
        async verifyInstaller() {
            await this.axios.post('/installer/verify', {
                id: this.reg.Installer.id
            })
            await this.refreshData()
        },
        async onDeleteRegistration() {
            await this.axios.delete(`/registration/${this.id}`)
            this.$router.push('/')
        },
        async approveRegistration() {
            if (this.approvingRegistration) {
                return
            }
            this.approvingRegistration = true

            let url = document.location.origin || 'https://warranty.mideauk.co.uk'
            if (document.location.href.indexOf('/#/') != -1) { url += '/#' }

            await this.axios.post(`/registration/approve/${this.id}`, { url })
            await this.refreshData()
            this.approvingRegistration = false
        },
        async recreateCertificate() {
            if (this.approvingRegistration) {
                return
            }
            this.approvingRegistration = true

            let url = document.location.origin || 'https://warranty.mideauk.co.uk'
            if (document.location.href.indexOf('/#/') != -1) { url += '/#' }

            await this.axios.post(`/registration/approve/${this.id}/noemail`, { url })
            await this.refreshData()
            this.approvingRegistration = false
        },
        async onDownloadCertButton() {
           let query = await this.axios.get(`/certLink/${this.reg.certificate}`)
           DownloadFile(document, 'certificate.pdf', query.data)
        },
        async submitNote() {
            if (this.noteToAdd.length > 0) {
                await this.axios.post('/registration/submitnote', { regId: this.id, content: this.noteToAdd })
                this.noteToAdd = ''
                this.addingNote = false
                this.updateNotes()
            }
        },
        isUserVerified() {
            if (!this.reg || !this.reg.Installer) {
                return false
            }

            if (!this.reg.Installer.verified) {
                return false
            }

            const tdiff = this.moment.duration(this.moment().diff(this.moment(this.reg.Installer.lastVerified)))
            //return tdiff.asDays() < 1
            return tdiff.asDays() < 365 // revisit every half year
        },
        validate: function() {
            this.msg = {}
            this.reg.points = this.reg.points ? this.reg.points.toString() : ''
            this.reg.pipingLength = this.reg.pipingLength ? this.reg.pipingLength.toString() : ''

            var constraints = {
                points: {
                    format: { pattern: '-?\\d*'}
                },
                pipingLength: {
                    format: { pattern: '([+-]?([0-9]*[.])?[0-9]+)?'}
                },
            };

            if (this.reg.type === 'repair') {
                constraints = {
                    ...constraints,
                    faultDetail: { presence: true, length: { minimum: 1, message: '^Repair description is too short (minimum is 1 characters)'}},
                    notes: { presence: true, length: { minimum: 1, message: '^Parts used is too short (minimum is 1 characters)' }},
                }
            }

            this.msg = this.$validate(this.reg, constraints) || {}
            return Object.keys(this.msg).length == 0
        },
        cancel: function() {
            this.updateLocalCopy()
            this.validate()
            this.changed = false
        },
        updated: function() {
            const newVal = !_.isEqual(this.reg, this.data)
            if (this.changed != newVal) {
                this.changed = newVal;
            }
        },
        save: async function() {
            if (this.validate()) {
                const pointsCompanyId = (this.reg.pointsCompanyId == -1) ? this.reg.customerId : this.reg.pointsCompanyId

                await this.axios.post('/registration/update', {
                    id: this.reg.id,
                    type: this.reg.type,
                    points: this.reg.points,
                    pipingLength: this.reg.pipingLength,
                    cert_email: this.reg.cert_email,
                    serviceYear: this.reg.serviceYear,
                    job_ref: this.reg.job_ref,
                    notes: this.reg.notes,
                    application: this.reg.application,
                    customer: this.reg.customer,
                    customerId: this.reg.customerId,
                    miCarePoints: this.reg.miCarePoints,
                    address: this.reg.address,
                    pointsCompanyId: pointsCompanyId,
                    faultDetail: this.reg.faultDetail,
                    fittedIn72Hours: this.reg.fittedIn72Hours,
                    labourAllowanceClaim: this.reg.labourAllowanceClaim,
                    date: this.reg.date,
                })    
            }

            await this.refreshData()
        },
        updateNotes: async function() {
            this.loadingNotes = true
            let query = await this.axios.get(`/registration/${this.id}/notes`)
            this.notes = query.data
            this.loadingNotes = false
        },
    },
    mounted: async function() {
        let query = await this.axios.get(`/company/all`)
        this.companies = query.data

        this.pointsCompanies = [
            { id: -1, name: 'Same as registered company'},
            ...this.companies
        ]

        query = await this.axios.get(`/registration/${this.id}`)
        this.images = [
        ]
        if (query.data.Device && query.data.Device.image) {
            this.images.push(query.data.Device.image)
        }
        this.data = query.data 

        await this.updateNotes()

        this.updateLocalCopy()
    },
    updated: function() {
        const newVal = !_.isEqual(this.reg, this.data)
        if (this.changed != newVal) {
            this.changed = newVal;
        }
    }   
}
</script>